/**
 * PII / secret redaction for analytics.
 *
 * URLs on this site can carry sensitive values in the query string (auth
 * tokens on reset-password/login callbacks, emails, one-time codes). GA4's
 * "Redact data" is inactive on the old stream, so this makes us strictly
 * safer than watch.bkfc.com: sensitive query params are stripped before any
 * page_location / page_path is sent to either sink.
 */

// Query-param keys (case-insensitive, substring match) that must never leave
// the browser in an analytics payload.
const SENSITIVE_KEYS = [
  'token',
  'access_token',
  'refresh_token',
  'id_token',
  'auth',
  'code',
  'otp',
  'password',
  'passwd',
  'pwd',
  'email',
  'jwt',
  'secret',
  'signature',
  'sig',
  'apikey',
  'api_key',
  'key',
  'session',
];

const isSensitive = key => {
  const k = key.toLowerCase();
  return SENSITIVE_KEYS.some(s => k.includes(s));
};

/**
 * Strip sensitive query params from a URL, returning a redacted absolute URL.
 * Redacted params keep their key but get a `[redacted]` value so we can still
 * see that (say) a reset flow occurred without leaking the token.
 *
 * @param {string} rawUrl - absolute URL (defaults to current location).
 * @returns {string} redacted absolute URL, or the input on parse failure.
 */
export const redactUrl = rawUrl => {
  const input = rawUrl || (typeof window !== 'undefined' ? window.location.href : '');
  if (!input) {
    return '';
  }
  try {
    const url = new URL(input);
    url.searchParams.forEach((_value, key) => {
      if (isSensitive(key)) {
        url.searchParams.set(key, '[redacted]');
      }
    });
    return url.toString();
  } catch (e) {
    // Not a full URL (or SSR). Best-effort: drop the query string entirely.
    return String(input).split('?')[0];
  }
};

/**
 * Redacted path + search (no origin) — what we send as GA4 `page_path`.
 *
 * @param {string} [rawUrl]
 * @returns {string}
 */
export const redactPath = rawUrl => {
  const input = rawUrl || (typeof window !== 'undefined' ? window.location.href : '');
  if (!input) {
    return '';
  }
  try {
    const url = new URL(input, 'https://bkfc.com');
    url.searchParams.forEach((_value, key) => {
      if (isSensitive(key)) {
        url.searchParams.set(key, '[redacted]');
      }
    });
    return `${url.pathname}${url.search}`;
  } catch (e) {
    return String(input).split('?')[0];
  }
};

/**
 * Shallow-redact an arbitrary params object (defensive: callers shouldn't put
 * secrets in event params, but this catches mistakes).
 *
 * @param {Object} params
 * @returns {Object}
 */
export const redactParams = params => {
  if (!params || typeof params !== 'object') {
    return params;
  }
  const out = {};
  Object.keys(params).forEach(key => {
    out[key] = isSensitive(key) ? '[redacted]' : params[key];
  });
  return out;
};

export const __test__ = { isSensitive, SENSITIVE_KEYS };
